import { useAppService } from "@fero/hooks";
import type {
  ContractNoteSourceRequest,
  ProgressRevaluationRequestResp,
  RevaluationRequestAction,
} from "adl-gen/ferovinum/app/api";
import { useAlert } from "components/context/global-alert/use-alert-context";
import { Loader } from "components/widgets/loader/loader";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useLoadingDataState } from "utils/hooks/use-loading-data";
import { isLoaded } from "utils/utility-types";
import { OrgRevaluationRequestDetailsPageView } from "./revaluation-request-details-page-view";

export const OrgRevaluationRequestDetailsPage = () => {
  const service = useAppService();
  const { revaluationRequestId } = useParams<{
    revaluationRequestId: string;
  }>();

  const loadRevaluationDetails = useCallback(async () => {
    const contractNoteFetchReq: ContractNoteSourceRequest = {
      kind: "revaluationRequestId",
      value: revaluationRequestId,
    };

    const [loadingRevaluationDetails, loadingContractNoteFetchResp] =
      await Promise.all([
        service.revaluationDetails(revaluationRequestId),
        service.getContractNoteView({
          kind: "sourceRequest",
          value: contractNoteFetchReq,
        }),
      ]);
    return { loadingRevaluationDetails, loadingContractNoteFetchResp };
  }, [service, revaluationRequestId]);

  const [loadingRevaluationDetails] = useLoadingDataState(
    loadRevaluationDetails,
  );
  const history = useHistory();
  const [showAlert] = useAlert();

  const handleAcknowledgeDealTerms = useCallback(
    async (response: () => Promise<ProgressRevaluationRequestResp>) => {
      const resp = await response();
      if (resp === "success") {
        await showAlert({
          title: "Success",
          body: "Deal terms acknowledged successfully",
          severity: "success",
        });
      } else {
        switch (resp) {
          case "invalidState":
            await showAlert({
              title: "Invalid Action",
              body: "You cannot perform this action on this request",
              severity: "error",
            });
            break;
          case "failure":
            await showAlert({
              title: "Error",
              body: "Sorry, something went wrong. Please try again",
              severity: "error",
            });
            break;
          default:
            break;
        }
      }
      setTimeout(() => {
        history.push({
          pathname: "/stock-revaluation",
          state: { from: "acknowledgeDealTerms" },
        });
      }, 500);
    },
    [showAlert, history],
  );

  const acknowledgeDealTerms = useCallback(
    async (
      accept: boolean,
    ): Promise<ProgressRevaluationRequestResp | undefined> => {
      let resp: ProgressRevaluationRequestResp | undefined;
      await handleAcknowledgeDealTerms(
        async (): Promise<ProgressRevaluationRequestResp> => {
          const action: RevaluationRequestAction = accept
            ? "accepted"
            : "rejected";
          resp = await service.progressRevaluationRequestState({
            revaluationRequestId,
            action,
          });
          await loadRevaluationDetails();
          return resp;
        },
      );
      return resp;
    },
    [
      revaluationRequestId,
      loadRevaluationDetails,
      handleAcknowledgeDealTerms,
      service,
    ],
  );

  return (
    <Loader loadingStates={[loadingRevaluationDetails]} fullScreen>
      {isLoaded(loadingRevaluationDetails) && (
        <OrgRevaluationRequestDetailsPageView
          revaluationDetails={
            loadingRevaluationDetails?.value.loadingRevaluationDetails
          }
          acknowledgeDealTerms={acknowledgeDealTerms}
          attachedDocuments={[
            ...loadingRevaluationDetails.value?.loadingContractNoteFetchResp
              ?.attachedDocuments,
            ...(loadingRevaluationDetails.value?.loadingRevaluationDetails
              ?.attachedDocuments ?? []),
          ]}
          referenceNumber={
            loadingRevaluationDetails.value?.loadingContractNoteFetchResp
              ?.dealNumber
          }
        />
      )}
    </Loader>
  );
};
